import React, {useCallback, useEffect, useReducer} from 'react';
import {BrowserRouter, Route, Switch} from 'react-router-dom';
import {User} from '@firebase/auth-types';

import Cod from './pages/Cod';
import SignIn from './pages/SignIn';
import Summary from './pages/Summary';
import NotFound from './pages/NotFound';
import PaymentDetail from './pages/PaymentDetail';
import PaymentMethod from './pages/PaymentMethod';
import PaymentPackage from './pages/PaymentPackage';

import {CssBaseline} from '@material-ui/core';
import ThemeProvider from './providers/ThemeProvider';
import PaymentDataProvider, {DataProvide} from './providers/PaymentDataProvider';
import MainSnackbar from './components/MainSnackbar';
import Protected from './components/Protected';
import Loading from './components/Loading';
import Context from './context';

import {useEffectOnce, usePrevious} from 'react-use';
import {useTranslation} from 'react-i18next';
import {useFirebaseAuth} from './hooks';

import StudentService from './services/student';
import SubscriptionService from './services/subscription';
import reducer, {
  initialState,
  setChosenPackage,
  setPartialState,
  setPromotion,
  setPackages,
  setLoading,
  setMessage,
  setToken,
} from './state-management/app';
import getKnowNative, {MIXPANEL} from './externals/mobile-communication';

import {
  ClientStorage,
  getMsgFromErr,
  parseQueryToField,
  getCommonMsgFromErr,
  resetPromotionOnPackage
} from './utils';
import {PROMOTION_STATUS} from './constants/enum';

import i18nIns from './localization';
import styles from './App.module.css';


const App: React.FC = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const {t} = useTranslation();
  const authenticator = useFirebaseAuth(fetchInitialData);

  const {promotion, user, packages, chosenPackage, loading} = state;

  const previousPromotion = usePrevious(promotion);

  useEffectOnce(() => { //1st hook
    const {lang} = parseQueryToField(window.location.search, ['lang']);
    if (lang) {
      i18nIns.changeLanguage(lang);
      ClientStorage.lang = lang;
    }
  });

  //hook for reset package state if promotion status change from valid to other state
  useEffect(() => {
    const isNewPromotionChangeFromValidToInvalid =
      previousPromotion &&
      previousPromotion.status === PROMOTION_STATUS.VALID &&
      promotion.status !== PROMOTION_STATUS.VALID;


    if (isNewPromotionChangeFromValidToInvalid) {
      dispatch(setPackages(resetPromotionOnPackage(packages)));
      //reset chosen package
      if (chosenPackage) {
        dispatch(setChosenPackage(resetPromotionOnPackage([chosenPackage])[0]));
      }
    }
  }, [promotion.status, previousPromotion, packages, chosenPackage]);


  useEffect(() => {

    const isPromotionChangeFromOtherToLoading =
      previousPromotion &&
      previousPromotion.status !== PROMOTION_STATUS.LOADING &&
      promotion.status === PROMOTION_STATUS.LOADING &&
      promotion.text !== '';

    if (isPromotionChangeFromOtherToLoading) {
      applyPromo();
    }

    async function applyPromo() {
      try {
        const packageWithPromotions = await SubscriptionService.applyPromoToPackage(
          packages,
          promotion.text,
          user!.country,
          state.token!
        );

        dispatch(setPackages(packageWithPromotions));

        if (chosenPackage) {
          const chosenPackageWithPromotions = packageWithPromotions.find((pack) => pack.id === chosenPackage.id);

          dispatch(setChosenPackage(chosenPackageWithPromotions || null));
        }

        if (packageWithPromotions.length > 0) {
          dispatch(setPromotion({
            status: PROMOTION_STATUS.VALID,
            expiryDate: packageWithPromotions[0].expiryDate,
            err: ''
          }));

          getKnowNative('mixpanel', undefined, {
            type: MIXPANEL.PAYMENT_APPLY_PROMOTION_CODE_SUCCESS,
            code: promotion.text,
            expiredDate: new Date(packageWithPromotions[0].expiryDate! * 1000).toISOString(),
          })
        }
      } catch (e) {
        const commonErrors = getCommonMsgFromErr(e);

        if (commonErrors) {
          dispatch(setMessage(commonErrors));

          dispatch(setPromotion({
            status: PROMOTION_STATUS.UNDETERMINED,
            err: ''
          }));
        } else {
          dispatch(setPromotion({
            status: PROMOTION_STATUS.INVALID,
            err: e.message
          }));
        }

        getKnowNative('mixpanel', undefined, {
          type: MIXPANEL.PAYMENT_APPLY_PROMOTION_CODE_FAILED
        })
      }
    }
  }, [previousPromotion, promotion, packages, chosenPackage, state.token, user]);


  async function fetchInitialData(user: User | null) {
    const {token: overrideToken} = parseQueryToField(window.location.search, ['token', 'planId']);
    let token = user ? await user.getIdToken(true) : undefined;

    token = overrideToken || token || ''; //override the firebase token if query has token

    if (!token) {
      dispatch(setLoading(false));
      return;
    }
    dispatch(setToken(token)); //todo: combine action to improve perf??

    try {
      let [student, plans, promotion, orders] = await Promise.all([
        StudentService.getStudentByToken(token),
        SubscriptionService.retrievePlans(token),
        SubscriptionService.retrieveCarriedOverPromotion(token),
        SubscriptionService.retrieveListOrder(token!)
      ]);

      if (student && student.profile) {
        dispatch(
          setPartialState({
            plans,
            orders,
            user: student.profile,
            promotion: {
              text: promotion.promotionCode,
              status: PROMOTION_STATUS.UNDETERMINED,
              err: ''
            }
          })
        )
      }

    } catch (e) {
      dispatch(setMessage(getMsgFromErr(e)));
    } finally {
      getKnowNative('mixpanel', undefined, {
        type: MIXPANEL.PAYMENT_WEBVIEW_LOAD_COMPLETE,
      });
      dispatch(setLoading(false));
    }
  }

  const onConfirmPromotionCode = async () => {
    dispatch(setPromotion({
      status: PROMOTION_STATUS.LOADING
    }));
  };

  const onChangePromotionCode = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setPromotion({
      text: e.target.value,
      status: PROMOTION_STATUS.UNDETERMINED,
      err: ''
    }));
  }, []);

  const onDataCallback = useCallback((data: DataProvide) => { //side effect

    dispatch(setPartialState(data));
  }, []);

  const onEndDataLoading = useCallback((err: string | null) => {
    if (err) {
      dispatch(setMessage(err));
    }

    dispatch(setPartialState({paymentLoading: false}))
  }, []);

  const isInitialized = authenticator.isInitialized && !loading;

  //todo: routing is wrong, cant display 404 page :((
  return (
    <div className={styles.app}>
      <ThemeProvider>
        <CssBaseline/>
        <Context.Provider value={dispatch}>
          <BrowserRouter>
            {
              isInitialized ?
                <Switch>
                  <Route path="/" exact render={(props) =>
                    <SignIn
                      {...props}
                      user={user}
                      plans={state.plans}
                      firebaseAuthenticator={authenticator}
                    />
                  }/>
                  <Route path="/summary/:status" render={(props) => <Summary {...props} {...state}/>}/>
                  {/*fallback for beautify path, now we dont use status in url anymore, but keep it for payment gateway redirect*/}
                  <Route path="/summary" render={(props) => <Summary {...props} {...state}/>}/>

                  <PaymentDataProvider
                    {...state}
                    dataCallback={onDataCallback}
                    onEndDataLoading={onEndDataLoading}
                  >
                    <Route path="/package" render={(props) =>
                      <Protected user={user} {...props}>
                        <PaymentPackage
                          {...state}
                          {...props}
                          onChange={onChangePromotionCode}
                          onConfirm={onConfirmPromotionCode}
                        />
                      </Protected>
                    }/>
                    <Route path="/detail" render={(props) =>
                      <Protected user={user} {...props}>
                        <PaymentDetail
                          {...state}
                          {...props}
                          onChange={onChangePromotionCode}
                          onConfirm={onConfirmPromotionCode}
                        />
                      </Protected>
                    }/>
                    <Route path="/method" render={(props) =>
                      <Protected user={user} {...props}>
                        <PaymentMethod
                          {...state}
                          {...props}
                        />
                      </Protected>
                    }/>
                    <Route path="/cod" render={(props) =>
                      <Protected user={state.user} {...props}>
                        <Cod
                          {...state}
                          {...props}
                        />
                      </Protected>
                    }/>
                  </PaymentDataProvider>
                  <Route component={NotFound}/>
                </Switch> :
                <Loading/>
            }
            <MainSnackbar
              open={Boolean(state.message)}
              variant="error"
              message={t(state.message)}
              onClose={() => dispatch(setMessage(''))}
            />
          </BrowserRouter>
        </Context.Provider>
      </ThemeProvider>
    </div>
  );
};

export default App;
